import type { ReactNode } from 'react';
import { Icon } from '../components/Icon';

// Selection checkbox used by multi-select tables/cards. Stops propagation so
// ticking a row never triggers the row's own click (drawer open / card edit).
export function CheckBox({ checked, onChange, indeterminate }: { checked: boolean; onChange: (v: boolean) => void; indeterminate?: boolean }) {
  const on = checked || indeterminate;
  return (
    <span
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className="cc-clickable"
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border-2)'}`, background: on ? 'var(--accent)' : 'var(--surface)', flex: '0 0 auto' }}
    >
      {(checked || indeterminate) && <Icon name={indeterminate && !checked ? 'remove' : 'check'} size={14} color="#fff" />}
    </span>
  );
}

// Floating action bar shown while a multi-select has ≥1 item selected.
export function BulkBar({ count, onClear, children }: { count: number; onClear: () => void; children?: ReactNode }) {
  if (!count) return null;
  return (
    <div style={{ position: 'fixed', left: '50%', bottom: 26, transform: 'translateX(-50%)', zIndex: 50, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 12, boxShadow: 'var(--shadow-md)', padding: '9px 10px 9px 15px', animation: 'ccfade .18s ease' }}>
      <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{count} selected</span>
      <span style={{ width: 1, height: 22, background: 'var(--border)' }} />
      {children}
      <span onClick={onClear} className="cc-clickable" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', padding: '6px 8px' }}>
        <Icon name="close" size={16} />Clear
      </span>
    </div>
  );
}

// A "set field" dropdown for the BulkBar: picking an option fires onPick and
// snaps back to the placeholder (value stays "").
export function BulkSelect({ label, icon, options, onPick }: { label: string; icon: string; options: { label: string; value: string }[]; onPick: (v: string) => void }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 8, padding: '0 7px 0 10px', height: 32 }}>
      <Icon name={icon} size={16} color="var(--text-3)" />
      <select value="" onChange={(e) => { if (e.target.value) onPick(e.target.value); }} style={{ appearance: 'none', border: 0, background: 'transparent', outline: 'none', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer', paddingRight: 16 }}>
        <option value="" disabled hidden>{label}</option>
        {options.map((o) => <option key={o.value} value={o.value} style={{ color: 'var(--text)', background: 'var(--surface)' }}>{o.label}</option>)}
      </select>
      <Icon name="expand_more" size={15} color="var(--text-3)" style={{ marginLeft: -20, pointerEvents: 'none' }} />
    </div>
  );
}

// Danger action pill for the BulkBar.
export function BulkAction({ label, icon, danger, onClick }: { label: string; icon: string; danger?: boolean; onClick: () => void }) {
  return (
    <span onClick={onClick} className="cc-clickable" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, height: 32, padding: '0 12px', borderRadius: 8, border: `1px solid ${danger ? 'var(--danger)' : 'var(--border-2)'}`, background: danger ? 'var(--danger-bg)' : 'var(--surface)', color: danger ? 'var(--danger)' : 'var(--text)' }}>
      <Icon name={icon} size={16} />{label}
    </span>
  );
}

export function Fade({ children }: { children: ReactNode }) {
  return <div style={{ animation: 'ccfade .25s ease' }}>{children}</div>;
}

export function SearchBox({ placeholder, value, onChange, width = 260 }: { placeholder: string; value: string; onChange: (v: string) => void; width?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 11px', width }}>
      <Icon name="search" size={18} color="var(--text-3)" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ border: 0, background: 'transparent', outline: 'none', fontFamily: 'inherit', fontSize: 13, color: 'var(--text)', width: '100%' }} />
    </div>
  );
}

// Compact native-select filter styled to match the toolbar chips.
export function FilterSelect({ icon, value, options, onChange }: { icon: string; value: string; options: { label: string; value: string }[]; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '0 9px 0 11px', height: 34 }}>
      <Icon name={icon} size={18} color="var(--text-3)" />
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ appearance: 'none', border: 0, background: 'transparent', outline: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, color: 'var(--text-2)', cursor: 'pointer', paddingRight: 18 }}>
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ color: 'var(--text)', background: 'var(--surface)' }}>{o.label}</option>
        ))}
      </select>
      <Icon name="expand_more" size={16} color="var(--text-3)" style={{ marginLeft: -22, pointerEvents: 'none' }} />
    </div>
  );
}

// Tab toggle (segmented control) for switching between table modes.
export function Tabs({ value, tabs, onChange }: { value: string; tabs: { label: string; value: string }[]; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'inline-flex', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3, gap: 3 }}>
      {tabs.map((t) => {
        const sel = t.value === value;
        return (
          <button key={t.value} type="button" onClick={() => onChange(t.value)} style={{ appearance: 'none', cursor: 'pointer', border: 0, borderRadius: 6, padding: '6px 14px', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, background: sel ? 'var(--surface)' : 'transparent', color: sel ? 'var(--text)' : 'var(--text-3)', boxShadow: sel ? 'var(--shadow-sm)' : 'none' }}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
